import { FC } from 'react';

import { getBannerIdForCopy } from 'utils/campaign/utils';
import { isAccountStatusReadOnly } from 'utils/clickme';
import { CampaignArchiveReason } from 'utils/constants/dashboard';

import useTrls from 'hooks/translation/useTrls';

import ArchiveReasonWrapper from 'components/CampaignCard/CampaignInfo/Actions/Status/Archived/ArchiveReasonWrapper';

import { Banner } from 'store/models/dashboard';
import { useSelector } from 'store/utils';

const urlToStatistics = (campaignId: number) => `/new/campaign/${campaignId}/statistics/`;
const getUrlToCopy = (campaignId: number, banners: Banner[]) =>
    `/new/campaign/${campaignId}/banner/${getBannerIdForCopy(banners)}/copy`;

interface ArchivedProps {
    archiveReason: CampaignArchiveReason | null;
    banners: Banner[];
    campaignId: number;
    isAuction: boolean;
}

const Archived: FC<ArchivedProps> = ({ banners, campaignId, archiveReason, isAuction }) => {
    const trl = useTrls();
    const currentAccountStatus = useSelector((state) => state.user.user?.currentAccount.status);

    const campaignArchiveReasonStatuses = {
        [CampaignArchiveReason.OldPrices]: (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.oldPrices"
                action={getUrlToCopy(campaignId, banners)}
                anchor="campaign.actions.status.copy.campaign"
            />
        ),
        [CampaignArchiveReason.ArchivedVacancy]: isAuction ? (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.auction.archivedVacancy"
            />
        ) : (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.archivedVacancy"
                action={getUrlToCopy(campaignId, banners)}
                anchor="campaign.actions.status.copy.campaign"
            />
        ),
        [CampaignArchiveReason.Transfer]: (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.transfer"
            />
        ),
        [CampaignArchiveReason.ObsoleteCampaign]: (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.obsoleteCampaign"
                action={getUrlToCopy(campaignId, banners)}
                anchor="campaign.actions.status.copy.campaign"
            />
        ),
        [CampaignArchiveReason.OldSource]: (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.oldSource"
                action={getUrlToCopy(campaignId, banners)}
                anchor="campaign.actions.status.copy.campaign"
            />
        ),
        [CampaignArchiveReason.ExpiredAccount]: (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.expiredAccount"
                action={urlToStatistics(campaignId)}
                anchor="campaign.actions.status.statistic.campaign"
            />
        ),
        [CampaignArchiveReason.OldBlockedCampaign]: isAuction ? (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.auction.oldBlockedCampaign"
            />
        ) : (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.oldBlockedCampaign"
                action={getUrlToCopy(campaignId, banners)}
                anchor="campaign.actions.status.copy.campaign"
            />
        ),
        [CampaignArchiveReason.WrongAccount]: isAuction ? (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.auction.wrongAccount"
            />
        ) : (
            <ArchiveReasonWrapper
                title="campaign.actions.status.archived.campaign"
                text="campaign.actions.status.archived.archiveReason.wrongAccount"
                action={getUrlToCopy(campaignId, banners)}
                anchor="campaign.actions.status.copy.campaign"
            />
        ),
    };

    if (isAccountStatusReadOnly(currentAccountStatus)) {
        return campaignArchiveReasonStatuses[CampaignArchiveReason.ExpiredAccount];
    }

    if (!archiveReason || !campaignArchiveReasonStatuses[archiveReason]) {
        return <div>{trl('campaign.actions.status.archived.campaign')}</div>;
    }

    return campaignArchiveReasonStatuses[archiveReason];
};

export default Archived;
